import { Card, CardContent } from "@/components/ui/card";
import { Mail, Phone, Calendar, Send, FileText } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type PersonActivity = {
  id: string;
  type: string;
  subject: string;
  description: string | null;
  activity_date: string;
  projects?: {
    name: string;
  };
  proposals?: {
    title: string;
  };
};

const getActivityIcon = (type: string) => {
  switch (type) {
    case "call": return Phone;
    case "email": return Mail;
    case "meeting": return Calendar;
    case "proposal": return Send;
    default: return FileText;
  }
};

const formatDateDistance = (date: string) => {
  const now = new Date();
  const activityDate = new Date(date);
  const diffInDays = Math.floor((now.getTime() - activityDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return `${Math.floor(diffInDays / 30)} months ago`;
};

interface ActivityFeedProps {
  personId: string;
}

export function ActivityFeed({ personId }: ActivityFeedProps) {
  const { data: activities = [], isLoading } = useQuery({
    queryKey: ["person-activities", personId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activities")
        .select(`
          *,
          projects (
            name
          ),
          proposals (
            title
          )
        `)
        .eq("person_id", personId)
        .order("activity_date", { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data as PersonActivity[];
    },
    enabled: !!personId
  });

  if (isLoading) {
    return <div className="text-center py-4">Loading activities...</div>;
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No activities found
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity) => {
        const IconComponent = getActivityIcon(activity.type);
        return (
          <Card key={activity.id}>
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <div className="bg-primary/10 p-2 rounded-full">
                  <IconComponent className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{activity.subject}</h3>
                    <div className="text-sm text-muted-foreground">
                      {formatDateDistance(activity.activity_date)}
                    </div>
                  </div>
                  
                  {activity.description && (
                    <p className="text-muted-foreground mb-2">{activity.description}</p>
                  )}
                  
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    {activity.projects?.name && (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        Project: {activity.projects.name}
                      </span>
                    )}
                    {activity.proposals?.title && (
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                        Proposal: {activity.proposals.title}
                      </span>
                    )}
                    <span className="capitalize">{activity.type}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}